import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import { object, ref, string } from "yup";
import { toast } from "react-toastify";
import { resetPassword } from "../../services/AuthServices";
import authIllustrations from "../../assets/images/auth-illustrations.png";
import "../Login/Auth.css";

const MainSection = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isDisable, setisDisable] = useState(false);

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    // Validate that token and email exist in URL
    if (!token || !email) {
      toast("Invalid reset link. Please request a new one.", { type: "error" });
      navigate("/forgot-password");
    }
  }, [token, email, navigate]);

  const AdminSchema = object().shape({
    password: string()
      .required("Please enter new password")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
        "Must contain 8+ chars, uppercase, lowercase and number"
      ),
    password_confirmation: string()
      .required("Please confirm your new password")
      .oneOf([ref("password")], "Passwords do not match"),
  });

  const submitClicked = async (data) => {
    setisDisable(true);
    let formdata = new FormData();
    formdata.append("email", email);
    formdata.append("token", token);
    formdata.append("password", data.password);
    formdata.append("password_confirmation", data.password_confirmation);

    await resetPassword(formdata)
      .then((response) => {
        setisDisable(false);
        toast("Password reset successfully!", { type: "success" });
        navigate("/login");
      })
      .catch((error) => {
        setisDisable(false);
        if (error?.response?.status == 422)
          toast(error?.response?.data?.message, { type: "error" });
        else if (error?.response?.status == 500)
          toast(error?.response.data?.message, { type: "error" });
        else
          toast(error?.response?.data?.message || "Something went wrong", { type: "error" });
      });
  };

  const { handleSubmit, handleBlur, handleChange, values, errors, touched } =
    useFormik({
      validationSchema: AdminSchema,
      initialValues: {
        password: "",
        password_confirmation: "",
      },
      onSubmit: async () => {
        submitClicked(values);
      },
      onReset: () => {},
    });

  return (
    <div className="dp-auth-container">
      <div className="dp-auth-card" style={{ maxWidth: '900px' }}>
        {/* Left Side - Illustration */}
        <div className="dp-auth-left">
          <div
            className="dp-auth-illustration-wrapper reset"
            style={{ backgroundImage: `url(${authIllustrations})` }}
          ></div>
          <div className="dp-auth-brand">
            <h2>Create New Password</h2>
            <p>Choose a strong password to keep your account secure.</p>
            <div className="dp-auth-features">
              <div className="dp-auth-feature">
                <i className="fas fa-check-circle"></i>
                <span>At least 8 characters</span>
              </div>
              <div className="dp-auth-feature">
                <i className="fas fa-check-circle"></i>
                <span>Uppercase & lowercase letters</span>
              </div>
              <div className="dp-auth-feature">
                <i className="fas fa-check-circle"></i>
                <span>Include numbers</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="dp-auth-right">
          <div className="dp-auth-header">
            <h1>Reset Password</h1>
            <p>Enter your new password below</p>
          </div>

          <form className="dp-auth-form" onSubmit={handleSubmit}>
            {/* New Password Field */}
            <div className="dp-form-group">
              <label className="dp-form-label">New Password</label>
              <div className="dp-form-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter new password"
                  className={`dp-form-input ${touched.password && errors.password ? 'error' : ''}`}
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  maxLength={50}
                />
                <i
                  className={`fal ${showPassword ? 'fa-eye' : 'fa-eye-slash'} dp-form-icon clickable`}
                  onClick={() => setShowPassword(!showPassword)}
                ></i>
              </div>
              {touched.password && errors.password && (
                <span className="dp-form-error">{errors.password}</span>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="dp-form-group">
              <label className="dp-form-label">Confirm Password</label>
              <div className="dp-form-input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="password_confirmation"
                  placeholder="Confirm new password"
                  className={`dp-form-input ${touched.password_confirmation && errors.password_confirmation ? 'error' : ''}`}
                  value={values.password_confirmation}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  maxLength={50}
                />
                <i
                  className={`fal ${showConfirmPassword ? 'fa-eye' : 'fa-eye-slash'} dp-form-icon clickable`}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                ></i>
              </div>
              {touched.password_confirmation && errors.password_confirmation && (
                <span className="dp-form-error">{errors.password_confirmation}</span>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="dp-auth-submit"
              disabled={isDisable}
            >
              {isDisable ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Resetting Password...
                </>
              ) : (
                <>
                  <i className="far fa-lock"></i>
                  Reset Password
                </>
              )}
            </button>

            {/* Footer */}
            <div className="dp-auth-footer">
              <p>
                Remember your password?{" "}
                <span onClick={() => navigate("/login")}>Back to Login</span>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MainSection;
